import React, { useState, useEffect, useRef } from 'react';
import { useFamilyContext } from '../../contexts/FamilyContext';
import { format } from 'date-fns';
import { 
  X, 
  Heart, 
  Share, 
  Star, 
  MapPin, 
  Users, 
  Calendar,
  Vote,
  Edit3,
  Clock,
  Camera,
  ChevronLeft,
  ChevronRight,
  Archive,
  DollarSign,
  Globe,
  Phone,
  AlertCircle,
  Menu,
  CheckCircle,
  CalendarCheck,
  UserCheck
} from 'lucide-react';
import AvatarOrInitial from './AvatarOrInitial';

export default function ActivityDetailPanel({ 
  activity, 
  isOpen, 
  onClose, 
  onEdit,
  onSuggestVote,
  onSchedule,
  originElement 
}) {
  const { currentUser, family, voteOnFamilyActivity, archiveFamilyActivity, markActivityCompleted, toggleActivityFavorite } = useFamilyContext();
  const [, setIsAnimating] = useState(false); // isAnimating not used but setIsAnimating is
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [rating, setRating] = useState(5);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [showCompletionForm, setShowCompletionForm] = useState(false);
  const [completionData, setCompletionData] = useState({
    participants: [],
    notes: '',
    postRating: 5
  });
  const panelRef = useRef(null);
  const backdropRef = useRef(null);

  // Check if current user has already voted
  const currentUserRating = activity?.ratings?.[currentUser?.id];
  const hasUserVoted = currentUserRating !== undefined;

  // Calculate family average and participation
  const familyRatings = Object.values(activity?.ratings || {});
  const familyAverage = familyRatings.length > 0 
    ? familyRatings.reduce((sum, rating) => sum + rating, 0) / familyRatings.length 
    : 0;

  // Get family member voting status
  const familyMembers = [...(family?.parents || []), ...(family?.kids || [])];
  const votingStatus = familyMembers.map(member => ({
    ...member,
    rating: activity?.ratings?.[member.id],
    hasVoted: activity?.ratings?.[member.id] !== undefined
  }));

  // Activity images (main image + additional images)
  const images = [
    ...(activity?.image ? [activity.image] : []),
    ...(activity?.additional_images || [])
  ].filter(Boolean);

  // Initialize rating if user has already voted
  useEffect(() => {
    if (hasUserVoted) {
      setRating(currentUserRating);
      setHasInteracted(true);
    }
  }, [hasUserVoted, currentUserRating, activity]);

  // Handle opening animation
  useEffect(() => {
    if (isOpen && originElement && panelRef.current) {
      setIsAnimating(true);
      
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      
      const originRect = originElement.getBoundingClientRect();
      const panel = panelRef.current;
      
      // Calculate center positions for proper scaling
      const originCenterX = originRect.left + originRect.width / 2;
      const originCenterY = originRect.top + originRect.height / 2;
      // screenCenterX and screenCenterY calculated but not currently used in animation
      
      // Set up panel to full screen size but scaled down
      panel.style.transition = 'none';
      panel.style.position = 'fixed';
      panel.style.top = '0px';
      panel.style.left = '0px';
      panel.style.width = '100vw';
      panel.style.height = '100vh';
      panel.style.borderRadius = '12px';
      panel.style.opacity = '1';
      panel.style.overflow = 'auto';
      panel.style.zIndex = '9999';
      panel.style.transformOrigin = `${originCenterX}px ${originCenterY}px`;
      
      // Calculate initial scale
      const scaleX = originRect.width / window.innerWidth;
      const scaleY = originRect.height / window.innerHeight;
      const scale = Math.min(scaleX, scaleY);
      
      // Apply initial transform
      panel.style.transform = `scale(${scale})`;
      
      // Hide content initially
      const content = panel.querySelector('.detail-content');
      if (content) {
        content.style.opacity = '0';
      }
      
      // Force reflow
      panel.getBoundingClientRect();
      
      // Now animate to full size
      requestAnimationFrame(() => {
        // Fade in backdrop
        if (backdropRef.current) {
          backdropRef.current.style.opacity = '0.4';
        }
        
        panel.style.transition = `
          transform 0.5s cubic-bezier(0.25, 0.8, 0.25, 1),
          border-radius 0.5s cubic-bezier(0.25, 0.8, 0.25, 1)
        `;
        
        panel.style.transform = 'scale(1)';
        panel.style.borderRadius = '0px';
        
        // Fade in content after expansion starts
        setTimeout(() => {
          if (content) {
            content.style.transition = 'opacity 0.3s ease-out';
            content.style.opacity = '1';
          }
          
          // Re-enable scrolling on the panel after animation
          panel.style.overflow = 'auto';
          setIsAnimating(false);
        }, 200);
      });
    }
    
    // Cleanup function to restore body scroll when component unmounts
    return () => {
      if (isOpen) {
        document.body.style.overflow = '';
        document.body.style.position = '';
        document.body.style.width = '';
      }
    };
  }, [isOpen, originElement]);

  // Handle vote submission
  const handleSubmitVote = async () => {
    if (!currentUser || !activity) return;

    try {
      await voteOnFamilyActivity(activity.id, currentUser.id, rating);
      setHasInteracted(true);
      // Close modal after successful vote submission
      handleClose();
    } catch (error) {
      console.error('Error submitting vote:', error);
    }
  };

  // Handle archive activity
  const handleArchive = async () => {
    if (!window.confirm('Are you sure you want to archive this activity? It will be hidden from active lists but preserved for future reference.')) {
      return;
    }
    
    try {
      await archiveFamilyActivity(activity.id);
      handleClose(); // Close the panel after archiving
    } catch (error) {
      console.error('Error archiving activity:', error);
      alert('Failed to archive activity. Please try again.');
    }
  };

  // Handle marking activity as completed
  const handleMarkCompleted = async () => {
    if (!showCompletionForm) {
      setShowCompletionForm(true);
      return;
    }

    try {
      await markActivityCompleted(activity.id, {
        completed_date: new Date().toISOString().split('T')[0],
        completed_by: completionData.participants,
        completion_notes: completionData.notes,
        post_ratings: { [currentUser.id]: completionData.postRating }
      });
      setShowCompletionForm(false);
      alert('Activity marked as completed!');
      handleClose();
    } catch (error) {
      console.error('Error marking activity as completed:', error);
      alert('Failed to mark activity as completed. Please try again.');
    }
  };

  // Format activity dates
  const formatActivityDate = () => {
    if (activity.completed_date) {
      return { 
        type: 'completed', 
        text: `Completed on ${format(new Date(activity.completed_date), 'MMMM d, yyyy')}`,
        icon: CheckCircle,
        color: 'text-emerald-600'
      };
    }
    if (activity.start_date) {
      const startDate = new Date(activity.start_date);
      if (activity.end_date && activity.end_date !== activity.start_date) {
        const endDate = new Date(activity.end_date);
        return { 
          type: 'range', 
          text: `${format(startDate, 'MMM d')} - ${format(endDate, 'MMM d, yyyy')}`,
          icon: Calendar,
          color: 'text-azure-600'
        };
      }
      return { 
        type: 'single', 
        text: format(startDate, 'MMMM d, yyyy'),
        icon: Calendar,
        color: 'text-azure-600'
      };
    }
    if (activity.date) {
      return { 
        type: 'single', 
        text: format(new Date(activity.date), 'MMMM d, yyyy'),
        icon: Calendar,
        color: 'text-azure-600'
      };
    }
    return null;
  };

  // Handle close with reverse animation
  const handleClose = () => {
    if (originElement && panelRef.current) {
      const panel = panelRef.current;
      const originRect = originElement.getBoundingClientRect();
      const content = panel.querySelector('.detail-content');
      
      // Calculate center for scaling back
      const originCenterX = originRect.left + originRect.width / 2;
      const originCenterY = originRect.top + originRect.height / 2;
      
      // First fade out content quickly
      if (content) {
        content.style.transition = 'opacity 0.15s ease-in';
        content.style.opacity = '0';
      }
      
      // Start fading backdrop immediately
      if (backdropRef.current) {
        backdropRef.current.style.transition = 'opacity 0.4s ease-in';
        backdropRef.current.style.opacity = '0';
      }
      
      // Then scale panel back to origin
      setTimeout(() => {
        panel.style.transformOrigin = `${originCenterX}px ${originCenterY}px`;
        panel.style.transition = `
          transform 0.4s cubic-bezier(0.5, 0, 0.3, 1),
          border-radius 0.4s cubic-bezier(0.5, 0, 0.3, 1)
        `;
        
        // Calculate scale back to origin size
        const scaleX = originRect.width / window.innerWidth;
        const scaleY = originRect.height / window.innerHeight;
        const scale = Math.min(scaleX, scaleY);
        
        panel.style.transform = `scale(${scale})`;
        panel.style.borderRadius = '12px';
        
        setTimeout(() => {
          // Restore body scroll before closing
          document.body.style.overflow = '';
          document.body.style.position = '';
          document.body.style.width = '';
          onClose();
        }, 400);
      }, 100);
    } else {
      // Restore body scroll before closing
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      onClose();
    }
  };

  // Handle escape key press
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    // Cleanup event listener
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [isOpen]); // Dependencies: isOpen

  // Get rating label
  const getRatingLabel = (value) => {
    if (value <= 2) return 'Not interested';
    if (value <= 4) return 'Might be okay';
    if (value <= 6) return 'Sounds good';
    if (value <= 8) return 'Really excited';
    return 'Super excited!';
  };

  // Format category display name
  const formatCategoryDisplay = (category) => {
    if (!category) return 'Other';
    
    const specialCases = {
      'dining-out': 'Dining Out',
      'casual-bites': 'Casual Bites',
      'food': 'Food & Dining',
      'sports': 'Sports & Fitness',
      'cultural': 'Arts & Culture'
    };
    
    if (specialCases[category.toLowerCase()]) {
      return specialCases[category.toLowerCase()];
    }
    
    return category
      .replace(/[-_]/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Get category emoji
  const getCategoryEmoji = (category) => {
    const emojiMap = {
      food: '🍕',
      entertainment: '🎬',
      outdoor: '🌳',
      sports: '⚽',
      cultural: '🎨',
      educational: '📚',
      social: '👥',
      creative: '🎨',
      adventure: '🏔️',
      relaxation: '🧘',
      shopping: '🛍️',
      other: '🎯'
    };
    return emojiMap[category] || '🎯';
  };

  if (!isOpen || !activity) return null;

  return (
    <div className="activity-detail-overlay">
      <div 
        ref={backdropRef}
        className="activity-detail-backdrop"
        onClick={handleClose}
      />
      <div 
        ref={panelRef} 
        className="activity-detail-panel"
      >
        {/* Header */}
        <div className="detail-header">
          <button
            onClick={handleClose}
            className="detail-close-btn"
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>
          
          <div className="detail-actions">
            <button className="detail-action-btn">
              <Share className="w-5 h-5" />
            </button>
            <button className="detail-action-btn">
              <Heart className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="detail-content">
          {/* Image Gallery */}
          <div className="detail-image-gallery">
            {images.length > 0 ? (
              <div className="relative">
                <img 
                  src={images[currentImageIndex]} 
                  alt={activity.title}
                  className="detail-main-image"
                />
                {images.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentImageIndex(prev => 
                        prev === 0 ? images.length - 1 : prev - 1
                      )}
                      className="detail-image-nav detail-image-prev"
                    >
                      <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => setCurrentImageIndex(prev => 
                        (prev + 1) % images.length
                      )}
                      className="detail-image-nav detail-image-next"
                    >
                      <ChevronRight className="w-5 h-5" />
                    </button>
                    <div className="detail-image-counter">
                      {currentImageIndex + 1} / {images.length}
                    </div>
                  </>
                )}
              </div>
            ) : (
              <div className="detail-placeholder-image">
                <div className="text-6xl mb-4">{getCategoryEmoji(activity.category)}</div>
                <Camera className="w-8 h-8 text-stone-400" />
              </div>
            )}
          </div>

          {/* Activity Info */}
          <div className="detail-info">
            <div className="detail-title-section">
              <h1 className="detail-title">{activity.title}</h1>
              
              {activity.location && (
                <div className="detail-location">
                  <MapPin className="w-4 h-4 text-stone-500" />
                  <span>{activity.location}</span>
                </div>
              )}

              <div className="detail-meta">
                <span className="detail-category">
                  {getCategoryEmoji(activity.category)} {formatCategoryDisplay(activity.category)}
                </span>
                
                {familyAverage > 0 && (
                  <div className="detail-rating">
                    <Star className="w-4 h-4 text-gold-500 fill-current" />
                    <span className="font-semibold">{familyAverage.toFixed(1)}</span>
                    <span className="text-stone-500">
                      ({familyRatings.length} rating{familyRatings.length !== 1 ? 's' : ''})
                    </span>
                  </div>
                )}
              </div>
              
              {/* Date Display */}
              {formatActivityDate() && (
                <div className={`flex items-center gap-2 mt-3 ${formatActivityDate().color}`}>
                  {React.createElement(formatActivityDate().icon, { className: 'w-5 h-5' })}
                  <span className="text-sm font-medium">{formatActivityDate().text}</span>
                </div>
              )}
              
              {/* Show participants if completed */}
              {activity.completed_by && activity.completed_by.length > 0 && (
                <div className="flex items-center gap-2 mt-2 text-stone-600">
                  <UserCheck className="w-4 h-4" />
                  <span className="text-sm">
                    Participants: {activity.completed_by.length} family member{activity.completed_by.length !== 1 ? 's' : ''}
                  </span>
                </div>
              )}
              
              {/* Completion notes */}
              {activity.completion_notes && (
                <div className="mt-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                  <p className="text-sm text-emerald-900">
                    <strong>Experience Notes:</strong> {activity.completion_notes}
                  </p>
                </div>
              )}
            </div>

            {/* Description */}
            {activity.description && (
              <div className="detail-description">
                <p>{activity.description}</p>
              </div>
            )}

            {/* Enhanced Details */}
            {(activity.pricing || activity.estimated_duration || activity.website || activity.phone || activity.age_range || activity.special_requirements || activity.menu_highlights) && (
              <div className="detail-enhanced-info">
                <h3 className="detail-section-title">Details</h3>
                <div className="enhanced-details-grid">
                  {/* Pricing */}
                  {activity.pricing && (
                    <div className="detail-info-item">
                      <DollarSign className="w-4 h-4 text-emerald-600" />
                      <div>
                        <span className="detail-info-label">Price</span>
                        <span className="detail-info-value">{activity.pricing.range}</span>
                        {activity.pricing.notes && (
                          <p className="detail-info-notes">{activity.pricing.notes}</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  {/* Duration */}
                  {activity.estimated_duration && (
                    <div className="detail-info-item">
                      <Clock className="w-4 h-4 text-azure-600" />
                      <div>
                        <span className="detail-info-label">Duration</span>
                        <span className="detail-info-value">{activity.estimated_duration}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Age Range */}
                  {activity.age_range && (
                    <div className="detail-info-item">
                      <Users className="w-4 h-4 text-violet-600" />
                      <div>
                        <span className="detail-info-label">Age Range</span>
                        <span className="detail-info-value">{activity.age_range}</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Website */}
                  {activity.website && (
                    <div className="detail-info-item">
                      <Globe className="w-4 h-4 text-azure-500" />
                      <div>
                        <span className="detail-info-label">Website</span>
                        <a 
                          href={activity.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="detail-info-link"
                        >
                          Visit Website
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {/* Phone */}
                  {activity.phone && (
                    <div className="detail-info-item">
                      <Phone className="w-4 h-4 text-emerald-500" />
                      <div>
                        <span className="detail-info-label">Phone</span>
                        <a href={`tel:${activity.phone}`} className="detail-info-link">
                          {activity.phone}
                        </a>
                      </div>
                    </div>
                  )}
                  
                  {/* Special Requirements */}
                  {activity.special_requirements && (
                    <div className="detail-info-item">
                      <AlertCircle className="w-4 h-4 text-orange-600" />
                      <div>
                        <span className="detail-info-label">Requirements</span>
                        <span className="detail-info-value">{activity.special_requirements}</span>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Menu Highlights / Pricing Notes (full width) */}
                {activity.menu_highlights && (
                  <div className="detail-menu-highlights">
                    <div className="flex items-center gap-2 mb-2">
                      <Menu className="w-4 h-4 text-amber-600" />
                      <span className="detail-info-label">Menu Highlights & Notes</span>
                    </div>
                    <p className="detail-info-notes">{activity.menu_highlights}</p>
                  </div>
                )}
              </div>
            )}

            {/* Family Ratings - Hide for completed activities */}
            {familyRatings.length > 0 && !activity.completed_date && (
              <div className="detail-family-ratings">
                <h3 className="detail-section-title">Family Ratings</h3>
                <div className="family-rating-list">
                  {votingStatus.filter(member => member.hasVoted).map((member) => (
                    <div key={member.id} className="family-rating-item">
                      <div className="family-member-info">
                        <AvatarOrInitial
                          avatar={member.avatar}
                          name={member.name}
                          size="sm"
                          className="family-member-avatar"
                        />
                        <span className="family-member-name">{member.name}</span>
                      </div>
                      <div className="family-member-rating">
                        <div className="rating-stars">
                          {Array.from({ length: 5 }, (_, i) => (
                            <Star
                              key={i}
                              className={`w-3 h-3 ${
                                i < (member.rating / 2) ? 'text-gold-500 fill-current' : 'text-stone-300'
                              }`}
                            />
                          ))}
                        </div>
                        <span className="rating-number">{member.rating}/10</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Your Rating - Hide for completed activities */}
            {!activity.completed_date && (
            <div className="detail-your-rating">
              <h3 className="detail-section-title">Your Rating</h3>
              
              <div className="rating-slider-section">
                <div className="rating-labels">
                  <span>Not interested</span>
                  <span>Super excited!</span>
                </div>
                
                <input
                  type="range"
                  min="0"
                  max="10"
                  value={rating}
                  onChange={(e) => {
                    setRating(parseInt(e.target.value));
                    setHasInteracted(true);
                  }}
                  className="rating-slider"
                />
              </div>

              <div className="current-rating-display">
                <div className="rating-value">{rating}</div>
                <div className="rating-label">{getRatingLabel(rating)}</div>
              </div>

              <button
                onClick={handleSubmitVote}
                disabled={!hasInteracted && hasUserVoted}
                className="submit-rating-btn"
              >
                {hasUserVoted && !hasInteracted ? 
                  `Your rating: ${currentUserRating}` : 
                  hasUserVoted ? 'Update Rating' : 'Submit Rating'
                }
              </button>
            </div>
            )}

            {/* Completion Form */}
            {showCompletionForm && !activity.completed_date && (
              <div className="detail-completion-form">
                <h3 className="detail-section-title">Mark as Completed</h3>
                
                <div className="space-y-4">
                  {/* Participant Selection */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Who participated?
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {familyMembers.map(member => (
                        <label key={member.id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={completionData.participants.includes(member.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setCompletionData(prev => ({
                                  ...prev,
                                  participants: [...prev.participants, member.id]
                                }));
                              } else {
                                setCompletionData(prev => ({
                                  ...prev,
                                  participants: prev.participants.filter(id => id !== member.id)
                                }));
                              }
                            }}
                            className="rounded border-stone-300"
                          />
                          <span className="text-sm">{member.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                  
                  {/* Post-Activity Rating */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      How was it? (1-10)
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="10"
                      value={completionData.postRating}
                      onChange={(e) => setCompletionData(prev => ({
                        ...prev,
                        postRating: parseInt(e.target.value)
                      }))}
                      className="w-full"
                    />
                    <div className="text-center text-lg font-bold mt-2">
                      {completionData.postRating}/10
                    </div>
                  </div>
                  
                  {/* Notes */}
                  <div>
                    <label className="block text-sm font-medium text-stone-700 mb-2">
                      Notes about the experience (optional)
                    </label>
                    <textarea
                      value={completionData.notes}
                      onChange={(e) => setCompletionData(prev => ({
                        ...prev,
                        notes: e.target.value
                      }))}
                      className="w-full px-3 py-2 border border-stone-300 rounded-lg focus:ring-2 focus:ring-gold-500 focus:border-gold-500"
                      rows={3}
                      placeholder="How did it go? Any memorable moments?"
                    />
                  </div>
                  
                  <div className="flex gap-2">
                    <button
                      onClick={handleMarkCompleted}
                      className="flex-1 bg-emerald-600 text-white py-2 px-4 rounded-lg hover:bg-emerald-700"
                    >
                      Save Completion
                    </button>
                    <button
                      onClick={() => setShowCompletionForm(false)}
                      className="flex-1 bg-stone-200 text-stone-700 py-2 px-4 rounded-lg hover:bg-stone-300"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Favorite Toggle for Completed Activities */}
            {activity.completed_date && (
              <div className="detail-favorite-section">
                <button
                  onClick={() => toggleActivityFavorite?.(activity.id, !activity.is_favorite)}
                  className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center gap-2 ${
                    activity.is_favorite 
                      ? 'bg-yellow-500 text-white hover:bg-yellow-600' 
                      : 'bg-stone-200 text-stone-700 hover:bg-stone-300'
                  }`}
                >
                  <Heart className={`w-5 h-5 ${activity.is_favorite ? 'fill-current' : ''}`} />
                  {activity.is_favorite ? 'Remove from Favorites' : 'Add to Favorites'}
                </button>
              </div>
            )}

            {/* Actions */}
            <div className="detail-actions-section">
              {!activity.completed_date && (
                <>
                  <button
                    onClick={handleMarkCompleted}
                    className="detail-action-button primary"
                  >
                    <CalendarCheck className="w-5 h-5 mr-2" />
                    Mark as Done
                  </button>
                  
                  <button
                    onClick={() => onSuggestVote?.(activity)}
                    className="detail-action-button secondary"
                  >
                    <Vote className="w-5 h-5 mr-2" />
                    Suggest for Vote
                  </button>
                  
                  <button
                    onClick={() => onSchedule?.(activity)}
                    className="detail-action-button secondary"
                  >
                    <Calendar className="w-5 h-5 mr-2" />
                    Add to Calendar
                  </button>
                </>
              )}
              
              <button
                onClick={() => onEdit?.(activity)}
                className="detail-action-button secondary"
              >
                <Edit3 className="w-5 h-5 mr-2" />
                Edit Activity
              </button>
              
              <button
                onClick={handleArchive}
                className="detail-action-button archive"
              >
                <Archive className="w-5 h-5 mr-2" />
                Archive Activity
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}